import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Form, Input, Select,
} from 'antd';
import EmrProviderAutocomplete from '../autocomplete/EmrProviderAutocomplete';

// NextGen caps a task description at 1000 characters, and the server appends
// "\n\nMedsender upload <id>"; Nextgen::TaskTemplateDescription reserves the same.
export const DESCRIPTION_MAX = 1000;
export const REFERENCE_RESERVE = 31;
export const TEXT_VALUE_MAX = 200;
const PROVIDER_TYPES = ['provider_search', 'practice_provider'];
const PRACTICE_SCOPED_TYPES = ['practice_provider', 'document_picklist'];

// Templates arrive in precedence order (own, department, organization), so the
// first one listing the document type is the one to apply.
export const templateForDocumentType = (templates, documentTypeId) => {
  if (!documentTypeId) return null;
  const wanted = String(documentTypeId).toLowerCase();
  return templates.find((template) => template.document_types.some((type) => type.id === wanted)) || null;
};

// answers: { [key]: { value, display, providerId } }
// The server strips text answers, so whitespace alone is no answer.
export const isAnswered = (answer) => String(answer?.value ?? '').trim() !== '';

export const templateDefaultAnswers = (template) => Object.fromEntries(
  (template?.fields || [])
    .filter((field) => field.default && ['select', 'text'].includes(field.type))
    .map((field) => [field.key, { value: field.default, display: field.default }]),
);

// The description lines the server will write, for the character counter.
export const templateLines = (template, answers) => (template?.fields || []).flatMap((field) => {
  const answer = answers[field.key];
  const display = String(answer?.display ?? '').trim();
  if (!isAnswered(answer) || !display) return [];
  const providerId = template.include_provider_ids && PROVIDER_TYPES.includes(field.type) && answer.providerId;
  return [`${field.label}: ${display}${providerId ? ` - ${answer.providerId}` : ''}`];
});

export const missingTemplateFields = (template, answers) => (template?.fields || [])
  .filter((field) => field.required && !isAnswered(answers[field.key]))
  .map((field) => field.label);

export const descriptionCharactersLeft = (lines, notes) => {
  const description = [lines.join('\n'), (notes || '').trim()].filter(Boolean).join('\n\n');
  return DESCRIPTION_MAX - REFERENCE_RESERVE - description.length;
};

export const templateSubmission = (template, answers) => ({
  id: template.id,
  values: Object.fromEntries(Object.entries(answers).map(([key, answer]) => [key, answer.value])),
});

// The Template picker and a chosen template's fields, at the top of the drawer's
// NextGen Task section (ENG-3780). The drawer owns the answers; practice-scoped
// choices (practice providers, NextGen pick lists) load for the selected practice.
const NextgenTaskTemplateFields = ({
  answers, categoryUnavailable, credentialId, lines, location, onAnswerChange, onTemplateChange, template, templates, triggerNote,
}) => {
  const [choices, setChoices] = useState({});
  const [choiceErrors, setChoiceErrors] = useState({});
  const answerRef = useRef(onAnswerChange);
  const previousScopeRef = useRef(null);

  useEffect(() => {
    answerRef.current = onAnswerChange;
  }, [onAnswerChange]);

  const scopedFields = (template?.fields || []).filter((field) => PRACTICE_SCOPED_TYPES.includes(field.type));
  const triggerField = template?.trigger_field_key
    ? template.fields.find((field) => field.key === template.trigger_field_key)
    : null;
  const templateId = template?.id;

  useEffect(() => {
    // A provider or pick-list option picked for one practice means nothing in another.
    const scope = `${templateId}:${location}`;
    const previous = previousScopeRef.current;
    if (previous && previous.templateId === templateId && previous.scope !== scope) {
      scopedFields.forEach((field) => answerRef.current(field.key, undefined));
    }
    previousScopeRef.current = { templateId, scope };
    setChoices({});
    setChoiceErrors({});
    if (!templateId || !location || !scopedFields.length) return undefined;

    let cancelled = false;
    Promise.all(scopedFields.map(async (field) => {
      try {
        const response = await axios.get(`/api/v1/nextgen_task_templates/${templateId}/field_options`, {
          params: { key: field.key, location },
        });
        if (!cancelled) setChoices((previousChoices) => ({ ...previousChoices, [field.key]: response.data.options }));
      } catch (error) {
        const message = error?.response?.data?.error || `Could not load ${field.label}.`;
        if (!cancelled) setChoiceErrors((previousErrors) => ({ ...previousErrors, [field.key]: message }));
      }
    }));
    return () => { cancelled = true; };
    // scopedFields derives from the template, which templateId identifies.
  }, [templateId, location]);

  const setAnswer = (field, value, display = value, providerId = undefined) => {
    onAnswerChange(field.key, value ? { value, display, providerId } : undefined);
  };

  const renderControl = (field) => {
    const answer = answers[field.key];
    switch (field.type) {
      case 'select':
        return (
          <Select
            allowClear
            placeholder="Select"
            value={answer?.value}
            options={field.options.map((option) => ({ value: option, label: option }))}
            onChange={(value) => setAnswer(field, value)}
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          />
        );
      case 'provider_search':
        return (
          <EmrProviderAutocomplete
            credentialId={credentialId}
            placeholder="Search referring providers..."
            value={answer?.value}
            onChange={(slug, provider) => setAnswer(
              field,
              slug,
              provider ? [provider.name, provider.degree].filter(Boolean).join(' ') : answer?.display,
              provider ? provider.emr_id : answer?.providerId,
            )}
          />
        );
      case 'practice_provider':
      case 'document_picklist': {
        const fieldChoices = choices[field.key] || [];
        return (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={location ? 'Select' : 'Pick a practice first'}
            disabled={!location}
            loading={Boolean(location) && !choices[field.key] && !choiceErrors[field.key]}
            value={answer?.value}
            options={fieldChoices}
            onChange={(value) => {
              const label = fieldChoices.find((option) => option.value === value)?.label;
              setAnswer(field, value, label, field.type === 'practice_provider' ? value : undefined);
            }}
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          />
        );
      }
      default:
        return (
          <Input
            value={answer?.value}
            maxLength={TEXT_VALUE_MAX}
            onChange={(event) => setAnswer(field, event.target.value)}
          />
        );
    }
  };

  return (
    // The drawer's own field classes, so these controls match the schema fields.
    <div className="nextgen-task-template-fields dynamic-ehr-drawer__subsection">
      <Form.Item
        className="dynamic-ehr-drawer__field"
        label="Template"
        extra={'Fills in the task from a saved template. Its answers go at the top of the task description'
          + ' as "Label: Value" lines.'}
      >
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="No template"
          value={template?.id}
          options={templates.map((option) => ({ value: option.id, label: option.name }))}
          onChange={(id) => onTemplateChange(id ?? null)}
          getPopupContainer={() => document.body}
          dropdownClassName="ehr-select-dropdown"
        />
      </Form.Item>
      {triggerNote && triggerField && (
        <Alert
          type="info"
          showIcon
          message={isAnswered(answers[triggerField.key])
            ? `A task will be created because ${triggerField.label} is filled in.`
            : `A task is created only if ${triggerField.label} is filled in. Leave it empty to file the document without a task.`}
        />
      )}
      {categoryUnavailable && (
        <Alert
          type="warning"
          showIcon
          message={`This template's category "${template.category_label}" is no longer in NextGen. Pick another category or leave it empty.`}
        />
      )}
      {(template?.fields || []).map((field) => (
        <Form.Item
          key={field.key}
          className="dynamic-ehr-drawer__field"
          label={field.label}
          required={field.required || (!triggerNote && field.key === triggerField?.key)}
          validateStatus={choiceErrors[field.key] ? 'error' : undefined}
          help={choiceErrors[field.key]}
        >
          {renderControl(field)}
        </Form.Item>
      ))}
      {template && !(triggerNote && triggerField && !isAnswered(answers[triggerField.key])) && (
        // What the answers add to the task description, exactly as NextGen gets them.
        // Hidden while an unanswered trigger field means no task will be created.
        <div className="nextgen-task-template-preview" data-testid="nextgen-task-template-preview">
          <div className="nextgen-task-template-preview__title">The task description will start with</div>
          {lines.length > 0 ? (
            <pre className="nextgen-task-template-preview__lines">{lines.join('\n')}</pre>
          ) : (
            <div className="nextgen-task-template-preview__empty">Answers appear here as &quot;Label: Value&quot; lines.</div>
          )}
        </div>
      )}
    </div>
  );
};

NextgenTaskTemplateFields.propTypes = {
  answers: PropTypes.objectOf(PropTypes.shape({
    value: PropTypes.string,
    display: PropTypes.string,
    providerId: PropTypes.string,
  })).isRequired,
  categoryUnavailable: PropTypes.bool,
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lines: PropTypes.arrayOf(PropTypes.string),
  location: PropTypes.string,
  onAnswerChange: PropTypes.func.isRequired,
  onTemplateChange: PropTypes.func.isRequired,
  template: PropTypes.shape({
    id: PropTypes.number,
    category_label: PropTypes.string,
    trigger_field_key: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.object),
  }),
  templates: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  triggerNote: PropTypes.bool,
};

NextgenTaskTemplateFields.defaultProps = {
  categoryUnavailable: false,
  lines: [],
  location: null,
  template: null,
  triggerNote: false,
};

export default NextgenTaskTemplateFields;
